import { superAdminApi } from "../authService";
import {
  ApiResponse,
  PaginatedApiResponse,
  PaginationParams,
} from "../../types/api";

export interface TenantTeamUser {
  id: number;
  tenant_id: number;
  global_identity_id: number | null;
  name: string;
  email: string;
  roles: string[];
  units: Array<{ id: number; name: string }>;
  professional: { id: number; name: string } | null;
  membership_status: string | null;
  invited_at: string | null;
  last_accessed_at: string | null;
  created_at: string | null;
}

export interface TenantTeamProfessional {
  id: number;
  tenant_id: number;
  name: string;
  email: string | null;
  country_code: string | null;
  phone_number: string | null;
  color_hex: string | null;
  commission_rate_bps: number | null;
  user_id: number | null;
  user: { id: number; name: string; email: string } | null;
  is_active: boolean;
  archived_at: string | null;
  created_at: string | null;
  updated_at: string | null;
}

export interface InviteUserPayload {
  email: string;
  name: string;
  role: string;
  units: number[];
  professional_id?: number | null;
}

export interface UpdateUserPayload {
  name?: string;
  email?: string;
  roles?: string[];
  units?: number[];
  professional_id?: number | null;
}

export interface ProfessionalPayload {
  name: string;
  color_hex: string;
  email?: string | null;
  country_code?: string | null;
  phone_number?: string | null;
  commission_rate_bps?: number | null;
  user_id?: number | null;
}

export const getTenantUnits = async (
  tenantId: number
): Promise<ApiResponse<Array<{ id: number; name: string }>>> => {
  const response = await superAdminApi.get<
    ApiResponse<Array<{ id: number; name: string }>>
  >(`/super-admin/tenants/${tenantId}/units`);
  return response.data;
};

export const getTenantUsers = async (
  tenantId: number,
  params?: PaginationParams & { search?: string }
): Promise<PaginatedApiResponse<TenantTeamUser>> => {
  const response = await superAdminApi.get<PaginatedApiResponse<TenantTeamUser>>(
    `/super-admin/tenants/${tenantId}/users`,
    { params }
  );
  return response.data;
};

export const inviteTenantUser = async (
  tenantId: number,
  payload: InviteUserPayload
): Promise<ApiResponse<{
  user: TenantTeamUser;
  membership: { id: number; status: string; invited_at: string | null };
  setup_required: boolean;
  invite_url: string;
}>> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      user: TenantTeamUser;
      membership: { id: number; status: string; invited_at: string | null };
      setup_required: boolean;
      invite_url: string;
    }>
  >(`/super-admin/tenants/${tenantId}/users/invite`, payload);
  return response.data;
};

export const updateTenantUser = async (
  tenantId: number,
  userId: number,
  payload: UpdateUserPayload
): Promise<ApiResponse<TenantTeamUser>> => {
  const response = await superAdminApi.patch<ApiResponse<TenantTeamUser>>(
    `/super-admin/tenants/${tenantId}/users/${userId}`,
    payload
  );
  return response.data;
};

export const revokeTenantUserMembership = async (
  tenantId: number,
  userId: number
): Promise<ApiResponse<TenantTeamUser>> => {
  const response = await superAdminApi.post<ApiResponse<TenantTeamUser>>(
    `/super-admin/tenants/${tenantId}/users/${userId}/revoke-membership`
  );
  return response.data;
};

export const getTenantProfessionals = async (
  tenantId: number,
  params?: PaginationParams & { search?: string; include_inactive?: boolean }
): Promise<PaginatedApiResponse<TenantTeamProfessional>> => {
  const response = await superAdminApi.get<
    PaginatedApiResponse<TenantTeamProfessional>
  >(`/super-admin/tenants/${tenantId}/professionals`, { params });
  return response.data;
};

export const createTenantProfessional = async (
  tenantId: number,
  payload: ProfessionalPayload
): Promise<ApiResponse<TenantTeamProfessional>> => {
  const response = await superAdminApi.post<ApiResponse<TenantTeamProfessional>>(
    `/super-admin/tenants/${tenantId}/professionals`,
    payload
  );
  return response.data;
};

export const updateTenantProfessional = async (
  tenantId: number,
  professionalId: number,
  payload: Partial<ProfessionalPayload>
): Promise<ApiResponse<TenantTeamProfessional>> => {
  const response = await superAdminApi.patch<ApiResponse<TenantTeamProfessional>>(
    `/super-admin/tenants/${tenantId}/professionals/${professionalId}`,
    payload
  );
  return response.data;
};

export const archiveTenantProfessional = async (
  tenantId: number,
  professionalId: number
): Promise<ApiResponse<TenantTeamProfessional>> => {
  const response = await superAdminApi.post<ApiResponse<TenantTeamProfessional>>(
    `/super-admin/tenants/${tenantId}/professionals/${professionalId}/archive`
  );
  return response.data;
};

export const restoreTenantProfessional = async (
  tenantId: number,
  professionalId: number
): Promise<ApiResponse<TenantTeamProfessional>> => {
  const response = await superAdminApi.post<ApiResponse<TenantTeamProfessional>>(
    `/super-admin/tenants/${tenantId}/professionals/${professionalId}/restore`
  );
  return response.data;
};

export const inviteTenantProfessional = async (
  tenantId: number,
  professionalId: number,
  email: string
): Promise<ApiResponse<{
  professional: TenantTeamProfessional;
  user: TenantTeamUser;
  membership: { id: number; status: string; invited_at: string | null };
  setup_required: boolean;
  invite_url: string;
}>> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      professional: TenantTeamProfessional;
      user: TenantTeamUser;
      membership: { id: number; status: string; invited_at: string | null };
      setup_required: boolean;
      invite_url: string;
    }>
  >(`/super-admin/tenants/${tenantId}/professionals/${professionalId}/invite`, {
    email,
  });
  return response.data;
};

export const impersonateUser = async (
  userId: number
): Promise<
  ApiResponse<{
    access_token: string;
    token_type: string;
    expires_in: number;
    user: { id: number; name: string; email: string; tenant_id: number };
  }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      access_token: string;
      token_type: string;
      expires_in: number;
      user: { id: number; name: string; email: string; tenant_id: number };
    }>
  >(`/super-admin/impersonate/${userId}`);
  return response.data;
};
